import {request} from './apiClient';
import {noteEditTracker} from './noteEditTracker';
import {scribePreferences} from './scribePreferencesService';
import {logger, LogFlow, describeError} from './logger';

// Scoped logger for note generation. We log template/section metadata, counts,
// durations, and error names — never transcript or note content (PHI).
const log = logger.scope(LogFlow.NoteGeneration);

// Max time to wait for the backend to return a generated note before aborting.
// Generation runs an LLM across every template section (and can hit the EHR for
// problem-list context), so legitimate runs take tens of seconds — but never
// minutes. 120s is comfortably above the slow-but-real ceiling while still
// guaranteeing a stuck request fails loudly instead of hanging the UI.
const NOTE_GENERATION_TIMEOUT_MS = 120_000;

// ── Built-in scribe templates (shared across all scribe UIs) ──

export const SCRIBE_TEMPLATES = [
  {
    // The id is intentionally kept as 'soap' so the ~15 callsites that default
    // selectedTemplateId to the literal 'soap' (ScribePage,
    // PostSessionNotesView, SessionDetailPanel, liveKitScribeSessionManager,
    // etc.) keep resolving to this template even after
    // the rename to "Progress Note". Sections are object-form with explicit
    // `order` so PostSessionNotesView's sectionMeta builder
    // (~line 1718, which skips string-typed entries) can sort them correctly
    // — the previous bare-string array silently fell through to JSON insertion
    // order, which was the root cause of the "Plan, Assessment, Objective"
    // ordering bug a customer reported.
    id: 'soap',
    name: 'Progress Note',
    description:
      'Standard progress note for general visits — Chief Complaint, HPI, ROS, General Exam, Assessment with ICD-10, and per-diagnosis Plan.',
    sections: [
      {name: 'Chief Complaint', order: 1},
      {name: 'History of Present Illness', order: 2},
      {name: 'Review of Systems', order: 3},
      {name: 'General Examination', order: 4},
      {name: 'Assessment', order: 5},
      {name: 'Plan', order: 6},
      {name: 'Preventative Medicine', order: 7},
    ],
    isDynamic: false,
  },
  {
    id: 'patient_intake',
    name: 'Patient Intake',
    description:
      'Initial assessment for new patients and first visits — Medical History, Medications, Social History, Assessment with ICD-10, and per-diagnosis Plan.',
    // Object-form sections with explicit `order` (see the 'soap' note above for
    // why bare-string arrays break section ordering). The free-text "Initial
    // Assessment" was replaced with the canonical ICD-coded `Assessment` + `Plan`
    // pair so intake visits (which our /suggest-template logic routes new-patient
    // encounters to) produce billable diagnosis codes like every other coded
    // template, instead of an uncoded narrative.
    sections: [
      {name: 'Chief Complaint', order: 1},
      {name: 'Medical History', order: 2},
      {name: 'Current Medications', order: 3},
      {name: 'Social History', order: 4},
      {name: 'Assessment', order: 5},
      {name: 'Plan', order: 6},
    ],
    isDynamic: false,
  },
  {
    id: 'after_visit_summary',
    name: 'After Visit Summary',
    description:
      'Patient-friendly summary of diagnosis, treatment, and follow-up',
    sections: [
      'Visit Details',
      'Diagnosis and Health Issues',
      'Medications',
      'Treatment and Procedures',
      'Test Results and Lab Work',
      'Follow-Up Instructions',
      'Referrals',
      'Patient Responsibilities',
    ],
    isDynamic: false,
  },
  {
    id: 'executive_summary',
    name: 'Executive Summary',
    description:
      'Dynamic meeting-style summary with key points and action items',
    sections: [],
    isDynamic: true,
  },
];

// ── Template lookup helpers ───────────────────────────────────

export function getTemplateById(templateId: any, allTemplates: any[] = []) {
  const pool = allTemplates.length > 0 ? allTemplates : SCRIBE_TEMPLATES;
  // No warning on miss: when EHR templates load, mergeAllTemplates drops the
  // built-in defaults, so the default `'soap'` id legitimately won't be in the
  // pool and falling back to the first EHR template is the expected behavior.
  return pool.find(t => t.id === templateId) || pool[0] || SCRIBE_TEMPLATES[0];
}

function getTemplateName(templateId: any, allTemplates: any[] = []) {
  const pool = allTemplates.length > 0 ? allTemplates : SCRIBE_TEMPLATES;
  return pool.find(t => t.id === templateId)?.name || 'Clinical Note';
}

/**
 * Merge templates into a single list, matching iOS resolvedTemplates() logic:
 * When EHR templates are available, built-in defaults are hidden — only
 * EHR + custom templates are returned. Otherwise all templates are shown.
 */
export function mergeAllTemplates(
  customTemplates: any[] = [],
  ehrTemplates: any[] = [],
) {
  const custom = customTemplates.map(t => ({
    ...t,
    isDynamic: Boolean(t.is_dynamic),
    isCustom: true,
  }));
  const ehr = ehrTemplates.map(t => ({
    ...t,
    isEHR: true,
  }));
  if (ehr.length > 0) {
    return [...ehr, ...custom];
  }
  return [...SCRIBE_TEMPLATES, ...custom];
}

/**
 * Suggest the best template for a conversation via backend LLM.
 * Matches iOS TemplateSuggestionService: sends transcript + available template metadata.
 * Returns { templateId, templateName } or null.
 */
export async function suggestTemplateForConversation(
  messages: any,
  availableTemplates: any[] = [],
) {
  const transcript = buildTranscriptForPrompt(messages);
  if (!transcript.trim()) return null;
  const snippet = transcript.slice(0, 6000);
  const templateNames = availableTemplates.map(t => ({
    id: t.id,
    name: t.name,
    description: t.description || '',
    specialty_category: t.specialty_category || '',
  }));
  try {
    const response = await request('/api/v1/scribe/suggest-template', {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify({
        transcript: snippet,
        template_names: templateNames,
      }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (data?.suggested_template_id) {
      return {
        templateId: data.suggested_template_id,
        templateName: data.suggested_template_name || '',
      };
    }
    return null;
  } catch {
    return null;
  }
}

// ── Transcript building ──────────────────────────────────────

export function buildTranscriptForPrompt(messages: any[] = []) {
  const lines = messages
    .filter(msg => !(msg.is_agent_message || msg.isAgentMessage))
    .filter(msg => (msg.content_type || msg.contentType) !== 'dictated_note')
    .map(msg => {
      // Multi-conversation encounter aggregation interleaves synthetic
      // "segment header" messages between source conversations so the LLM
      // can attribute statements to the chart-prep vs. the live visit
      // contributor. Emit them as a blank-line-separated header rather than
      // a `Speaker: text` line — the LLM treats them as section dividers
      // and we avoid the surface-level appearance of a speaker turn.
      if (
        msg.is_segment_marker ||
        msg.content_type === 'segment_header' ||
        msg.contentType === 'segment_header'
      ) {
        const header = (msg.input_text || msg.output_text || '').trim();
        if (!header) return null;
        return `\n${header}\n`;
      }
      const inputText = (msg.input_text || '').trim();
      const outputText = (msg.output_text || '').trim();
      const contentText = (msg.content || '').trim();
      const text = inputText || contentText;
      if (!text && !outputText) return null;
      const speaker =
        msg.explicit_meaning ||
        msg.explicitMeaning ||
        (msg.is_doctor ? 'Provider' : 'Patient');
      if (outputText && outputText !== inputText) {
        return `${speaker}: ${inputText}\n${speaker} (translated): ${outputText}`;
      }
      return `${speaker}: ${text}`;
    })
    .filter(Boolean);
  return lines.join('\n');
}

// ── Extract structured notes from session messages ───────────

// Lenient timestamp coercion — Firestore Timestamps, JS Dates, ISO
// strings, and `{seconds, nanoseconds}` plain objects all show up here
// depending on whether the message came from Firestore directly or was
// hydrated from a cache/REST layer. Returns `null` for unknown shapes
// rather than throwing so an editedAt mismatch never crashes the
// session detail view.
function coerceTimestamp(value: any) {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d?.getTime?.()) ? null : d;
  }
  if (typeof value === 'object') {
    const seconds = value.seconds ?? value._seconds;
    const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      const d = new Date(seconds * 1000 + Math.floor(nanos / 1e6));
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function extractAllStructuredNotes(
  messages: any[] = [],
  customTemplates: any[] = [],
) {
  return messages
    .filter(
      msg =>
        msg.content_type === 'dictated_note' ||
        msg.contentType === 'dictated_note' ||
        msg.structured_output,
    )
    .map(msg => {
      const structured = msg.structured_output || msg.structuredOutput;
      const sections = structured?.sections;
      if (
        !sections ||
        typeof sections !== 'object' ||
        Object.keys(sections).length === 0
      ) {
        return null;
      }
      const templateId =
        structured.template_id ||
        structured.templateId ||
        structured.note_type ||
        'soap';
      const storedTemplateName =
        structured.template_name ||
        structured.templateName ||
        structured.note_type_name ||
        structured.noteTypeName;
      const generatedAt = coerceTimestamp(msg.timestamp);
      const editedAt = coerceTimestamp(msg.edited_at ?? msg.editedAt);
      // PHI-free fingerprint + time of the last EHR push, stamped by
      // patientService.updatePushedSnapshotForSession. Carried through so the
      // note view can detect edits made since the last push even after a
      // remount (navigate away/back) or on another device.
      const lastPushedSignature =
        msg.last_pushed_signature ?? msg.lastPushedSignature ?? null;
      const lastPushedAt = coerceTimestamp(
        msg.last_pushed_at ?? msg.lastPushedAt,
      );
      // Schema V2: notes materialized through the data layer carry their
      // EncounterNote id on the mirrored legacy message. Surfacing it lets
      // edit/push flows route through dlUpdateNoteSection / dlMarkNotePushed
      // instead of the legacy message-update path.
      const encounterNoteId =
        structured.encounter_note_id || structured.encounterNoteId || null;
      return {
        id: msg.id,
        ...(encounterNoteId ? {noteId: encounterNoteId} : {}),
        templateId,
        templateName:
          storedTemplateName || getTemplateName(templateId, customTemplates),
        sections,
        generatedAt,
        editedAt,
        lastPushedSignature,
        lastPushedAt,
        rawTranscription: msg.input_text || msg.inputText || '',
      };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => {
      const aTs = a.editedAt || a.generatedAt || 0;
      const bTs = b.editedAt || b.generatedAt || 0;
      return bTs - aTs;
    });
}

/**
 * Extract only the latest structured note from a conversation transcript.
 */
export function extractExistingStructuredNote(
  messages: any[] = [],
  customTemplates: any[] = [],
) {
  const all = extractAllStructuredNotes(messages, customTemplates);
  if (!all.length) return null;
  return {...all[0], isFromHistory: true};
}

// ── Assessment ↔ Treatment/Plan reconciliation ───────────────
// Sections are generated in parallel, so Treatment/Plan may reference
// ICD-10 codes that the Assessment section omitted. This step extracts
// any codes present in Treatment headers but missing from Assessment
// and appends them so the two sections stay in sync.

// `\.\w+` (not `\.\d{1,4}`) so 7th-character / X-placeholder codes
// (e.g. S82.432A, T36.0X1A) match as valid ICD-10 codes and Plan headers.
const ICD10_PATTERN = /[A-Z]\d{2}(?:\.\w+)?/g;
const TREATMENT_HEADER_RE = /^\[([A-Z]\d{2}(?:\.\w+)?)]\s*(.+?)\s*:?\s*$/gm;
const ASSESSMENT_KEYS = ['Assessment', 'Assessment / Diagnosis', 'Assessments'];
const TREATMENT_KEYS = [
  'Plan',
  'Treatment/Plan',
  'Treatment / Plan',
  'Treatment',
];

// AI-scribe disclaimer (CIFC 2026-05-17). The backend appends this line
// at the end of the Assessment and Plan as part of /api/v1/scribe/
// generate-note post-processing. We mirror the helper here as defense-in-
// depth so a future backend regression that drops the line doesn't silently
// propagate to the doctor-facing UI.
const AI_SCRIBE_DISCLAIMER =
  'Opalite AI Scribe was used to help generate this note.';
const DISCLAIMER_DETECTION_RE =
  /Opalite\s+AI\s+Scribe\s+was\s+used\s+to\s+help\s+generate\s+this\s+note/i;
const DISCLAIMER_TARGET_NAMES = [
  // Combined A&P sections first.
  'Assessment and Plan',
  'Assessment & Plan',
  // Plan / Treatment sections — the natural end of a SOAP note.
  ...TREATMENT_KEYS,
  // Assessment fallback for templates that have no Plan.
  ...ASSESSMENT_KEYS,
];

function appendAiScribeDisclaimer(sections: any) {
  if (!sections || typeof sections !== 'object') return;
  const lowerToKey = new Map();
  for (const key of Object.keys(sections)) {
    lowerToKey.set(key.toLowerCase(), key);
  }
  let target = null;
  for (const name of DISCLAIMER_TARGET_NAMES) {
    const hit = lowerToKey.get(name.toLowerCase());
    if (hit) {
      target = hit;
      break;
    }
  }
  if (!target) return;
  const current = typeof sections[target] === 'string' ? sections[target] : '';
  if (DISCLAIMER_DETECTION_RE.test(current)) return;
  const trimmed = current.replace(/\s+$/, '');
  sections[target] = trimmed
    ? `${trimmed}\n\n${AI_SCRIBE_DISCLAIMER}`
    : AI_SCRIBE_DISCLAIMER;
}

function reconcileAssessmentAndTreatment(sections: any) {
  const assessmentKey = ASSESSMENT_KEYS.find(k => sections[k] != null);
  const treatmentKey = TREATMENT_KEYS.find(k => sections[k] != null);
  if (!assessmentKey || !treatmentKey) return;

  const assessmentText = sections[assessmentKey];
  const treatmentText = sections[treatmentKey];

  const assessmentCodes = new Set(
    (assessmentText.match(ICD10_PATTERN) || []).map((c: any) =>
      c.toUpperCase(),
    ),
  );

  const missingDiagnoses = [];
  let match;
  while ((match = TREATMENT_HEADER_RE.exec(treatmentText)) !== null) {
    const code = match[1].toUpperCase();
    const name = match[2].replace(/\s*:$/, '').trim();
    if (!assessmentCodes.has(code)) {
      missingDiagnoses.push({code, name});
      assessmentCodes.add(code);
    }
  }

  if (missingDiagnoses.length === 0) return;

  const existingNumbers = assessmentText.match(/^\d+\./gm) || [];
  let nextNum =
    existingNumbers.length > 0
      ? Math.max(...existingNumbers.map((n: any) => parseInt(n, 10))) + 1
      : 1;

  let appended = assessmentText.trimEnd();
  for (const {code, name} of missingDiagnoses) {
    appended += `\n${nextNum}. ${name} - ${code}`;
    nextNum++;
  }
  sections[assessmentKey] = appended;
}

// ── Note generation ──────────────────────────────────────────

// ── Failure classification for backend-draft recovery ────────
//
// POST /api/v1/scribe/generate-note is not transactional from the client's
// point of view: when the request dies client-side (timeout, dropped
// connection, gateway hiccup) the backend usually keeps going, finishes the
// note, and upserts it onto the conversation as a draft message (Cloud Run
// logs `scribe_generate_note_complete` → `scribe_draft_upserted`). The
// 2026-07-02 incident (Conversation ec04b4eb-…) is the canonical case: the
// client aborted at 45s, the backend saved the finished note at 53s, and the
// clinician's retries regenerated from scratch, burning 45–55s each. Errors
// of that class are tagged here so callers can poll for the just-saved draft
// (see recoverNoteFromBackendDraft below) instead of showing a hard error.
const DRAFT_RECOVERY_ELIGIBLE_FLAG = 'backendMayHaveSavedDraft';

function markDraftRecoveryEligible(err: any) {
  if (err && typeof err === 'object') {
    try {
      err[DRAFT_RECOVERY_ELIGIBLE_FLAG] = true;
    } catch (_) {
      // Frozen/sealed error object — the caller just loses the recovery hint.
    }
  }
  return err;
}

/**
 * True when a generate-note failure is of a class where the backend may have
 * completed anyway and saved the note as a draft — i.e. it is worth checking
 * the conversation for a just-upserted draft before surfacing a hard error.
 */
export function isDraftRecoveryEligibleError(err: any) {
  return Boolean(
    err && typeof err === 'object' && err[DRAFT_RECOVERY_ELIGIBLE_FLAG],
  );
}

// ── Failure classification for the streaming generate path ──
//
// generateNoteFromConversationStream distinguishes two failure classes:
//   - The stream broke without an authoritative verdict (transport error
//     mid-stream, stall timeout, stream ended without a `complete` event).
//     These are tagged here so the caller can retry ONCE via the legacy
//     non-streaming request.
//   - The backend delivered a verdict (a non-200 response, or an `error`
//     event on the 200 stream). Retrying would just repeat the failure, so
//     these stay untagged and surface through the caller's normal error
//     handling.
const STREAM_FALLBACK_ELIGIBLE_FLAG = 'streamFallbackToLegacyEligible';

function markStreamFallbackEligible(err: any) {
  if (err && typeof err === 'object') {
    try {
      err[STREAM_FALLBACK_ELIGIBLE_FLAG] = true;
    } catch (_) {
      // Frozen/sealed error object — the caller just loses the retry hint.
    }
  }
  return err;
}

/**
 * True when a streaming generate-note failure happened without an
 * authoritative backend verdict, i.e. it is worth one legacy non-streaming
 * retry before surfacing the error.
 */
export function isStreamFallbackEligibleError(err: any) {
  return Boolean(
    err && typeof err === 'object' && err[STREAM_FALLBACK_ELIGIBLE_FLAG],
  );
}

/**
 * Generate a clinical note from a conversation transcript using the
 * specified template via the centralized backend generator.
 *
 * @param params
 * @param params.messages     - Conversation messages
 * @param params.template     - Template object with { id, name, sections, isDynamic }
 * @param params.patientName  - Patient display name
 * @param params.providerName - Provider display name
 * @param [params.documents]  - Retained for caller compatibility; backend context is not changed here
 * @returns Generated note with { templateId, templateName, sections, rawTranscription, generatedAt }
 */
/**
 * Builds the exact JSON body POSTed to /api/v1/scribe/generate-note. Shared
 * between the legacy single-response path (generateNoteViaBackend) and the
 * streaming path (generateNoteViaBackendStream), which adds `stream: true`.
 */
function buildGenerateNoteRequestBody({
  messages,
  template,
  patientName,
  providerName,
  specialty,
  conversationId,
  patientId,
  ehrPatientId,
  encounterId,
  generationMode,
  existingSections,
}: Record<string, any>) {
  const formatting = scribePreferences.getFormatting();
  // One-off "this note only" instructions. Remembered instructions are no
  // longer sent from the client — the backend reads them authoritatively
  // from the user's Firestore record and sanitizes them server-side.
  const customInstructions = scribePreferences.getEphemeralInstructions();
  const noteLength = scribePreferences.getNoteLength();
  const sectionNoteLengths = scribePreferences.getSectionNoteLengths(
    template?.id,
  );
  const editContext = noteEditTracker.getPromptContext();

  return {
    messages,
    template: {
      id: template?.id,
      name: template?.name,
      isDynamic: Boolean(template?.isDynamic),
      is_dynamic: Boolean(template?.isDynamic),
      sections: template?.sections || [],
    },
    preferences: {
      formatting,
      customInstructions: customInstructions || '',
      noteLength,
      sectionNoteLengths,
      editContext: editContext || '',
    },
    patientName: patientName || '',
    providerName: providerName || '',
    specialty: specialty || '',
    conversationId: conversationId || null,
    patientId: patientId || null,
    // The patient's EHR id (eCW account number) — distinct from `patientId`
    // (the Opalite id used for document context). The backend needs this to
    // fetch the problem list so the note reuses the chart's existing codes.
    ...(ehrPatientId ? {ehrPatientId} : {}),
    // When the EHR encounter is already known (e.g. re-generating from the
    // push flow), pass it so the backend can reuse codes already on that
    // encounter. Usually null at first generation (encounter is picked at
    // push); the backend also falls back to the conversation's linkage.
    ...(encounterId ? {encounterId} : {}),
    ...(generationMode ? {generationMode} : {}),
    ...(existingSections ? {existingSections} : {}),
  };
}

/**
 * Normalizes the backend's generated-note payload (`data` from the legacy
 * envelope, or the `note` object on the streaming `complete` event — the
 * backend sends the identical object in both) into the shape the app's note
 * views expect. Defense-in-depth: we still run cleanSectionContent +
 * reconcileAssessmentAndTreatment on the response; on a well-formed backend
 * response these are effectively no-ops.
 */
function normalizeGeneratedNoteData(
  data: Record<string, any>,
  {template, messages}: Record<string, any>,
) {
  const sections = data?.sections || {};

  Object.keys(sections).forEach(name => {
    sections[name] = cleanSectionContent(name, sections[name]);
  });
  reconcileAssessmentAndTreatment(sections);
  // The backend already appends this disclaimer; this is a defense-in-depth
  // pass that catches a backend regression without duplicating the line
  // when the backend behaves correctly (idempotent).
  appendAiScribeDisclaimer(sections);

  return {
    templateId: data?.templateId || template?.id || 'soap',
    templateName: data?.templateName || template?.name || 'Clinical Note',
    sections,
    sectionMeta: data?.sectionMeta || {},
    rawTranscription:
      data?.rawTranscription || buildTranscriptForPrompt(messages),
    generatedAt: data?.generatedAt ? new Date(data.generatedAt) : new Date(),
    isFromHistory: Boolean(data?.isFromHistory),
    documentContext: data?.documentContext || null,
    // Per-ICD-10-code provenance so the note preview can badge each diagnosis
    // ("On problem list" / "Already in encounter"). Array of
    // { code, onProblemList, inEncounter }; empty when the backend had no
    // problem-list / encounter context.
    codeProvenance: Array.isArray(data?.codeProvenance)
      ? data.codeProvenance
      : [],
  };
}

/**
 * Calls the centralized backend /api/v1/scribe/generate-note endpoint and
 * Defense-in-depth: we still run cleanSectionContent + reconcileAssessmentAndTreatment
 * on the response; on a well-formed backend response these are effectively no-ops.
 */
async function generateNoteViaBackend({
  messages,
  template,
  patientName,
  providerName,
  specialty,
  conversationId,
  patientId,
  ehrPatientId,
  encounterId,
  generationMode,
  existingSections,
}: Record<string, any>) {
  // Defense-in-depth at the network boundary: never POST an empty transcript.
  // The public entry point (generateNoteFromConversation) already throws on an
  // empty transcript, but guarding here too means no caller of this helper can
  // send `messages: []` — which the backend rejects with a 400 and which, on
  // iOS, was firing before the session's messages had loaded. Mirrors the iOS
  // ScribeBackendClient empty-transcript guard.
  if (!buildTranscriptForPrompt(messages).trim()) {
    throw new Error('No transcript content available for this conversation.');
  }

  const body = buildGenerateNoteRequestBody({
    messages,
    template,
    patientName,
    providerName,
    specialty,
    conversationId,
    patientId,
    ehrPatientId,
    encounterId,
    generationMode,
    existingSections,
  });

  console.log('[scribeService] POST /api/v1/scribe/generate-note', {
    templateId: template?.id,
    generationMode: generationMode || 'final',
    conversationId: conversationId || null,
    messageCount: messages?.length || 0,
    sectionCount: template?.sections?.length || 0,
  });
  const startedAt = Date.now();
  log.info('generate_note_started', {
    template_id: template?.id || null,
    generation_mode: generationMode || 'final',
    conversation_id: conversationId || null,
    encounter_id: encounterId || null,
    has_ehr_patient: Boolean(ehrPatientId),
    message_count: messages?.length || 0,
    template_section_count: template?.sections?.length || 0,
  });
  // Bound the request: a hung connection (or a backend that accepts the socket
  // but never responds) must not leave the caller's "Generating…" state stuck
  // forever. We abort after NOTE_GENERATION_TIMEOUT_MS and surface a friendly,
  // retryable error rather than spinning indefinitely.
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    NOTE_GENERATION_TIMEOUT_MS,
  );
  let response;
  try {
    response = await request('/api/v1/scribe/generate-note', {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify(body),
      signal: controller.signal,
      // The NOTE_GENERATION_TIMEOUT_MS controller above is the sole bound.
      // request()'s 45s DEFAULT_REQUEST_TIMEOUT_MS is too tight for full-note
      // generation on long visits (legitimately 45–60s+) and would abort a
      // request the backend goes on to complete.
      timeoutMs: 0,
    });
  } catch (err) {
    if (controller.signal.aborted) {
      log.error('generate_note_timed_out', {
        template_id: template?.id || null,
        conversation_id: conversationId || null,
        timeout_ms: NOTE_GENERATION_TIMEOUT_MS,
        duration_ms: Date.now() - startedAt,
      });
      // Our own timeout fired while the backend was mid-generation — the
      // classic "backend finishes seconds later and upserts a draft" case.
      throw markDraftRecoveryEligible(
        new Error(
          'Note generation timed out. Please check your connection and try again.',
        ),
      );
    }
    log.error('generate_note_request_threw', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    // The request died on the wire without a verdict from the backend:
    // either fetch's network failure (always a TypeError in browsers —
    // "Failed to fetch" / "Load failed") or apiClient's default-timeout
    // abort (ApiError with a null status; every other ApiError carries the
    // HTTP status). In both cases the backend usually completes the
    // generation anyway. Errors thrown before the request went out (e.g.
    // "Not authenticated") don't match either shape and stay ineligible.
    if (
      err instanceof TypeError ||
      (err?.name === 'ApiError' && err.status == null)
    ) {
      markDraftRecoveryEligible(err);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
  if (!response.ok) {
    const errorBody = await response.text();
    log.error('generate_note_failed', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      status: response.status,
      duration_ms: Date.now() - startedAt,
    });
    const httpError = new Error(
      `Backend note generation failed (${response.status}): ${errorBody || 'Unknown error'}`,
    );
    // Gateway-level failures mean the load balancer/proxy gave up on the
    // request, not that the app rejected it — the generation itself often
    // still completes. App-level statuses (4xx, 500) are a real verdict:
    // no draft will appear, so don't send callers polling for one.
    if ([502, 503, 504].includes(response.status)) {
      markDraftRecoveryEligible(httpError);
    }
    throw httpError;
  }
  let payload;
  try {
    payload = await response.json();
  } catch (err) {
    log.error('generate_note_response_unreadable', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    // The backend answered 200 — generation finished and (for
    // conversation-linked runs) the draft is saved; only reading the body
    // failed client-side.
    throw markDraftRecoveryEligible(
      new Error(
        'The generated note could not be read from the server. Please check your connection and try again.',
      ),
    );
  }
  const data = payload?.data || payload;
  const normalized = normalizeGeneratedNoteData(data, {template, messages});

  log.info('generate_note_succeeded', {
    template_id: normalized.templateId,
    generation_mode: generationMode || 'final',
    conversation_id: conversationId || null,
    section_count: Object.keys(normalized.sections).length,
    from_history: normalized.isFromHistory,
    code_provenance_count: normalized.codeProvenance.length,
    duration_ms: Date.now() - startedAt,
  });

  return normalized;
}

/**
 * Streaming variant of generateNoteViaBackend. POSTs the exact same body
 * plus `stream: true` and branches on the response content-type:
 *   - `application/x-ndjson`: the backend streams one JSON event per line —
 *     `start` (template section names, in order), `section` (preliminary
 *     per-section content, out of order), `ping` (heartbeat), `complete`
 *     (the authoritative note — same object as the legacy envelope's
 *     `data`), `error` (terminal generation failure on the 200 stream).
 *   - anything else: the deployed backend predates streaming and ignored
 *     the flag — parse the legacy single-JSON envelope exactly like
 *     generateNoteViaBackend and resolve without progressive events.
 *
 * The NOTE_GENERATION_TIMEOUT_MS bound still applies, but the deadline is
 * re-armed every time ANY stream traffic arrives (pings included) so a
 * long-but-healthy stream isn't killed; only a stall aborts.
 *
 * Throws on `error` events and non-200 responses (authoritative verdicts —
 * no retry hint) and throws stream-fallback-eligible errors (see
 * isStreamFallbackEligibleError) when the stream dies without a `complete`.
 */
async function generateNoteViaBackendStream({
  messages,
  template,
  patientName,
  providerName,
  specialty,
  conversationId,
  patientId,
  ehrPatientId,
  encounterId,
  generationMode,
  existingSections,
  onStart,
  onSection,
}: Record<string, any>) {
  // Same network-boundary guard as the legacy path.
  if (!buildTranscriptForPrompt(messages).trim()) {
    throw new Error('No transcript content available for this conversation.');
  }

  const body = {
    ...buildGenerateNoteRequestBody({
      messages,
      template,
      patientName,
      providerName,
      specialty,
      conversationId,
      patientId,
      ehrPatientId,
      encounterId,
      generationMode,
      existingSections,
    }),
    stream: true,
  };

  console.log('[scribeService] POST /api/v1/scribe/generate-note (stream)', {
    templateId: template?.id,
    generationMode: generationMode || 'final',
    conversationId: conversationId || null,
    messageCount: messages?.length || 0,
    sectionCount: template?.sections?.length || 0,
  });
  const startedAt = Date.now();
  log.info('generate_note_started', {
    template_id: template?.id || null,
    generation_mode: generationMode || 'final',
    conversation_id: conversationId || null,
    encounter_id: encounterId || null,
    has_ehr_patient: Boolean(ehrPatientId),
    message_count: messages?.length || 0,
    template_section_count: template?.sections?.length || 0,
    stream: true,
  });

  // Stall guard: the same 120s bound the legacy path uses, but re-armed on
  // every stream event (pings included) — the timeout only fires when the
  // connection goes silent, never on a healthy long generation.
  const controller = new AbortController();
  let timeoutId = setTimeout(
    () => controller.abort(),
    NOTE_GENERATION_TIMEOUT_MS,
  );
  const extendDeadline = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(
      () => controller.abort(),
      NOTE_GENERATION_TIMEOUT_MS,
    );
  };

  let response;
  try {
    response = await request('/api/v1/scribe/generate-note', {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify(body),
      signal: controller.signal,
      // The stall-guard controller above is the sole bound (see the legacy
      // path for why request()'s 45s default is too tight for note work).
      timeoutMs: 0,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    // Failures before the stream starts mirror the legacy path exactly —
    // same classification, same draft-recovery tagging, no stream-fallback
    // hint (the legacy request would die on the same wire).
    if (controller.signal.aborted) {
      log.error('generate_note_timed_out', {
        template_id: template?.id || null,
        conversation_id: conversationId || null,
        timeout_ms: NOTE_GENERATION_TIMEOUT_MS,
        duration_ms: Date.now() - startedAt,
        stream: true,
      });
      throw markDraftRecoveryEligible(
        new Error(
          'Note generation timed out. Please check your connection and try again.',
        ),
      );
    }
    log.error('generate_note_request_threw', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      stream: true,
      ...describeError(err),
    });
    if (
      err instanceof TypeError ||
      (err?.name === 'ApiError' && err.status == null)
    ) {
      markDraftRecoveryEligible(err);
    }
    throw err;
  }
  if (!response.ok) {
    clearTimeout(timeoutId);
    // Errors before the stream starts (auth/validation) arrive as normal
    // non-200 JSON — identical handling to the legacy path.
    const errorBody = await response.text();
    log.error('generate_note_failed', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      status: response.status,
      duration_ms: Date.now() - startedAt,
      stream: true,
    });
    const httpError = new Error(
      `Backend note generation failed (${response.status}): ${errorBody || 'Unknown error'}`,
    );
    if ([502, 503, 504].includes(response.status)) {
      markDraftRecoveryEligible(httpError);
    }
    throw httpError;
  }

  const contentType = (
    response.headers.get('content-type') || ''
  ).toLowerCase();
  if (!contentType.includes('application/x-ndjson')) {
    // The deployed backend predates streaming and returned the legacy
    // single-JSON envelope — parse it exactly like generateNoteViaBackend
    // and resolve without any progressive events.
    clearTimeout(timeoutId);
    let payload;
    try {
      payload = await response.json();
    } catch (err) {
      log.error('generate_note_response_unreadable', {
        template_id: template?.id || null,
        conversation_id: conversationId || null,
        duration_ms: Date.now() - startedAt,
        stream: true,
        ...describeError(err),
      });
      throw markDraftRecoveryEligible(
        new Error(
          'The generated note could not be read from the server. Please check your connection and try again.',
        ),
      );
    }
    const data = payload?.data || payload;
    const normalized = normalizeGeneratedNoteData(data, {template, messages});
    log.info('generate_note_succeeded', {
      template_id: normalized.templateId,
      generation_mode: generationMode || 'final',
      conversation_id: conversationId || null,
      section_count: Object.keys(normalized.sections).length,
      from_history: normalized.isFromHistory,
      code_provenance_count: normalized.codeProvenance.length,
      duration_ms: Date.now() - startedAt,
      stream: false,
    });
    return normalized;
  }

  // ── NDJSON event stream ──
  let finalNote: any = null;
  let streamErrorMessage: string | null = null;
  let eventCount = 0;
  const handleEvent = (event: any) => {
    if (!event || typeof event !== 'object') return;
    eventCount += 1;
    switch (event.type) {
      case 'start':
        onStart?.(Array.isArray(event.sections) ? event.sections : []);
        break;
      case 'section':
        if (event.name) {
          onSection?.({
            name: event.name,
            content: typeof event.content === 'string' ? event.content : '',
            tier: event.tier ?? null,
          });
        }
        break;
      case 'ping':
        // Heartbeat — its arrival already re-armed the stall deadline.
        break;
      case 'complete':
        finalNote = normalizeGeneratedNoteData(event.note || {}, {
          template,
          messages,
        });
        break;
      case 'error':
        streamErrorMessage =
          (typeof event.error === 'string' && event.error.trim()) ||
          'Note generation failed.';
        break;
      default:
        // Forward-compat: unknown event types are ignored.
        break;
    }
  };

  try {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const {done, value} = await reader.read();
      if (done) break;
      // Any traffic proves the stream is alive — re-arm the stall guard.
      extendDeadline();
      buffer += decoder.decode(value, {stream: true});
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        let event;
        try {
          event = JSON.parse(trimmed);
        } catch {
          // Garbled line — skip it rather than killing the stream.
          continue;
        }
        handleEvent(event);
        if (streamErrorMessage) break;
      }
      if (streamErrorMessage) {
        // Terminal generation failure delivered over the 200 stream —
        // nothing more is coming; release the connection.
        try {
          await reader.cancel();
        } catch {
          /* already closed */
        }
        break;
      }
    }
    // A final event without a trailing newline still counts.
    const tail = buffer.trim();
    if (tail && !streamErrorMessage) {
      try {
        handleEvent(JSON.parse(tail));
      } catch {
        // Truncated tail — the missing-`complete` check below decides.
      }
    }
  } catch (err) {
    clearTimeout(timeoutId);
    // The stream died mid-flight without a backend verdict. The backend
    // usually keeps generating (and saving the draft) regardless, so tag
    // for draft recovery like the legacy path — and for the one-shot
    // legacy retry the streaming callers layer on top.
    if (controller.signal.aborted) {
      log.error('generate_note_timed_out', {
        template_id: template?.id || null,
        conversation_id: conversationId || null,
        timeout_ms: NOTE_GENERATION_TIMEOUT_MS,
        duration_ms: Date.now() - startedAt,
        stream: true,
      });
      throw markStreamFallbackEligible(
        markDraftRecoveryEligible(
          new Error(
            'Note generation timed out. Please check your connection and try again.',
          ),
        ),
      );
    }
    log.error('generate_note_stream_read_failed', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      events_received: eventCount,
      ...describeError(err),
    });
    throw markStreamFallbackEligible(markDraftRecoveryEligible(err));
  } finally {
    clearTimeout(timeoutId);
  }

  if (streamErrorMessage) {
    // An authoritative failure verdict — surface it as-is, no retry hint.
    log.error('generate_note_stream_error_event', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      events_received: eventCount,
    });
    throw new Error(streamErrorMessage);
  }
  if (!finalNote) {
    log.error('generate_note_stream_incomplete', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      events_received: eventCount,
    });
    throw markStreamFallbackEligible(
      markDraftRecoveryEligible(
        new Error(
          'The generated note could not be read from the server. Please check your connection and try again.',
        ),
      ),
    );
  }

  log.info('generate_note_succeeded', {
    template_id: finalNote.templateId,
    generation_mode: generationMode || 'final',
    conversation_id: conversationId || null,
    section_count: Object.keys(finalNote.sections).length,
    from_history: finalNote.isFromHistory,
    code_provenance_count: finalNote.codeProvenance.length,
    duration_ms: Date.now() - startedAt,
    stream: true,
  });
  return finalNote;
}

/**
 * Regenerates a single section of an already-generated note via the backend
 * /api/v1/scribe/regenerate-section endpoint, honoring the per-section
 * verbosity override the provider just picked (stored in scribePreferences
 * under `sectionNoteLengths[templateId][sectionName]`). Only the named
 * section is rebuilt from the transcript; the caller swaps the returned
 * content into that one section and leaves the rest of the note untouched.
 */
export async function regenerateNoteSection({
  messages,
  template,
  sectionName,
  patientName = '',
  providerName = '',
  specialty = '',
  conversationId = null,
  patientId = null,
  existingSections = null,
}: Record<string, any>) {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('No transcript available to regenerate this section.');
  }
  if (!template) {
    throw new Error('Template is required.');
  }
  if (!sectionName || !String(sectionName).trim()) {
    throw new Error('Section name is required.');
  }

  const formatting = scribePreferences.getFormatting();
  // One-off "this note only" instructions. Remembered instructions are no
  // longer sent from the client — the backend reads them authoritatively
  // from the user's Firestore record and sanitizes them server-side.
  const customInstructions = scribePreferences.getEphemeralInstructions();
  const noteLength = scribePreferences.getNoteLength();
  const sectionNoteLengths = scribePreferences.getSectionNoteLengths(
    template?.id,
  );
  const editContext = noteEditTracker.getPromptContext();

  const body = {
    messages,
    template: {
      id: template?.id,
      name: template?.name,
      isDynamic: Boolean(template?.isDynamic),
      is_dynamic: Boolean(template?.isDynamic),
      sections: template?.sections || [],
    },
    sectionName,
    preferences: {
      formatting,
      customInstructions: customInstructions || '',
      noteLength,
      sectionNoteLengths,
      editContext: editContext || '',
    },
    patientName: patientName || '',
    providerName: providerName || '',
    specialty: specialty || '',
    conversationId: conversationId || null,
    patientId: patientId || null,
    ...(existingSections ? {existingSections} : {}),
  };

  const startedAt = Date.now();
  log.info('regenerate_section_started', {
    template_id: template?.id || null,
    section_name: sectionName,
    conversation_id: conversationId || null,
    message_count: messages.length,
  });
  let response;
  try {
    response = await request('/api/v1/scribe/regenerate-section', {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify(body),
      // Section-level LLM work shares generate-note's latency profile on long
      // visits; request()'s 45s default is too tight.
      timeoutMs: NOTE_GENERATION_TIMEOUT_MS,
    });
  } catch (err) {
    log.error('regenerate_section_request_threw', {
      template_id: template?.id || null,
      section_name: sectionName,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    throw err;
  }
  if (!response.ok) {
    const errorBody = await response.text();
    log.error('regenerate_section_failed', {
      template_id: template?.id || null,
      section_name: sectionName,
      conversation_id: conversationId || null,
      status: response.status,
      duration_ms: Date.now() - startedAt,
    });
    throw new Error(
      `Section regeneration failed (${response.status}): ${errorBody || 'Unknown error'}`,
    );
  }
  const payload = await response.json();
  const data = payload?.data || payload;
  const returnedName = data?.sectionName || sectionName;
  log.info('regenerate_section_succeeded', {
    template_id: template?.id || null,
    section_name: returnedName,
    conversation_id: conversationId || null,
    duration_ms: Date.now() - startedAt,
  });
  // Defense-in-depth: run the same per-section cleanup the full generator
  // applies, so a backend hiccup can't surface raw model scaffolding.
  const content = cleanSectionContent(returnedName, data?.content || '');

  return {
    sectionName: returnedName,
    content,
    tier: data?.tier || null,
    generatedBy: data?.generatedBy || null,
    codeProvenance: Array.isArray(data?.codeProvenance)
      ? data.codeProvenance
      : [],
  };
}

/**
 * Applies a short post-visit dictation to an already-generated note by
 * calling the centralized backend /api/v1/scribe/edit-note endpoint.
 *
 * The backend arbitrates between verbatim insertion and command-style edits
 * using the physician's cursor context, and returns the full updated
 * `sections` map plus a human-readable `editSummary`, the inferred mode, and
 * a list of changed section names.
 *
 * @param params
 * @param params.sections         - Current note sections { [name]: string }
 * @param params.transcript       - Final dictated transcript (non-empty)
 * @param params.template         - Template descriptor (id, name, sections[])
 * @param params.activeSection    - Section the cursor was in, if any
 * @param params.cursorSnippet    - Text window with "[CURSOR]" marker
 * @param params.patientName
 * @param params.providerName
 * @param params.specialty
 * @param params.conversationId
 * @param params.patientId
 */
export async function editNoteWithDictation({
  sections,
  transcript,
  template,
  activeSection = null,
  cursorSnippet = null,
  patientName = '',
  providerName = '',
  specialty = '',
  conversationId = null,
  patientId = null,
}: Record<string, any>) {
  if (
    !sections ||
    typeof sections !== 'object' ||
    Object.keys(sections).length === 0
  ) {
    throw new Error('Current note sections are required.');
  }
  const trimmed = (transcript || '').trim();
  if (!trimmed) {
    throw new Error('Dictation transcript is empty.');
  }
  if (!template) {
    throw new Error('Template is required.');
  }

  const formatting = scribePreferences.getFormatting();
  // One-off "this note only" instructions. Remembered instructions are no
  // longer sent from the client — the backend reads them authoritatively
  // from the user's Firestore record and sanitizes them server-side.
  const customInstructions = scribePreferences.getEphemeralInstructions();
  const noteLength = scribePreferences.getNoteLength();
  const editContext = noteEditTracker.getPromptContext();

  const body = {
    sections,
    transcript: trimmed,
    activeSection,
    cursorSnippet,
    template: {
      id: template?.id,
      name: template?.name,
      isDynamic: Boolean(template?.isDynamic),
      is_dynamic: Boolean(template?.isDynamic),
      sections: template?.sections || [],
    },
    preferences: {
      formatting,
      customInstructions: customInstructions || '',
      noteLength,
      editContext: editContext || '',
    },
    patientName: patientName || '',
    providerName: providerName || '',
    specialty: specialty || '',
    conversationId: conversationId || null,
    patientId: patientId || null,
  };

  console.log('[scribeService] POST /api/v1/scribe/edit-note', {
    templateId: template?.id,
    activeSection,
    hasCursorSnippet: Boolean(cursorSnippet),
    transcriptLength: trimmed.length,
    sectionCount: Object.keys(sections).length,
  });
  const startedAt = Date.now();
  log.info('dictation_edit_started', {
    template_id: template?.id || null,
    conversation_id: conversationId || null,
    has_active_section: Boolean(activeSection),
    has_cursor_snippet: Boolean(cursorSnippet),
    transcript_length: trimmed.length,
    section_count: Object.keys(sections).length,
  });

  let response;
  try {
    response = await request('/api/v1/scribe/edit-note', {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify(body),
      // Dictation edits rewrite sections across the full note — same latency
      // profile as generation; request()'s 45s default is too tight.
      timeoutMs: NOTE_GENERATION_TIMEOUT_MS,
    });
  } catch (err) {
    log.error('dictation_edit_request_threw', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      duration_ms: Date.now() - startedAt,
      ...describeError(err),
    });
    throw err;
  }
  if (!response.ok) {
    // Prefer the structured `error` field from our API envelope so we don't
    // surface raw JSON (or any stray vendor names the backend couldn't scrub)
    // into doctor-facing toasts. Fall back to a generic phrase on any parse
    // failure.
    let friendlyError =
      'We couldn\u2019t apply that dictation. Please try again.';
    try {
      const parsed = await response.clone().json();
      if (parsed && typeof parsed.error === 'string' && parsed.error.trim()) {
        friendlyError = parsed.error.trim();
      }
    } catch (_) {
      // Non-JSON body — ignore; keep the generic message.
    }
    log.error('dictation_edit_failed', {
      template_id: template?.id || null,
      conversation_id: conversationId || null,
      status: response.status,
      duration_ms: Date.now() - startedAt,
    });
    throw new Error(friendlyError);
  }
  const payload = await response.json();
  const data = payload?.data || payload;

  // Defense-in-depth: run the same post-processing we do for full note
  // generation so any stray markdown headers or missing-from-assessment
  // diagnoses are cleaned up on the client too.
  const nextSections = {...(data?.sections || {})};
  Object.keys(nextSections).forEach(name => {
    nextSections[name] = cleanSectionContent(name, nextSections[name]);
  });
  reconcileAssessmentAndTreatment(nextSections);
  // The backend always re-asserts the disclaimer after a dictation edit;
  // this client-side pass is idempotent and only matters when the backend
  // version drifts. See appendAiScribeDisclaimer for details.
  appendAiScribeDisclaimer(nextSections);

  const changedSectionNames = Array.isArray(data?.changedSectionNames)
    ? data.changedSectionNames
    : [];
  log.info('dictation_edit_succeeded', {
    template_id: template?.id || null,
    conversation_id: conversationId || null,
    mode_inferred: data?.modeInferred || 'noop',
    changed_section_count: changedSectionNames.length,
    duration_ms: Date.now() - startedAt,
  });

  return {
    sections: nextSections,
    editSummary: data?.editSummary || '',
    modeInferred: data?.modeInferred || 'noop',
    changedSectionNames,
    generatedAt: data?.generatedAt || new Date().toISOString(),
  };
}

export async function generateNoteFromConversation({
  messages,
  template,
  patientName,
  providerName,
  specialty,
  documents,
  conversationId,
  patientId,
  ehrPatientId,
  encounterId,
  generationMode,
  existingSections,
}: Record<string, any>) {
  const transcript = buildTranscriptForPrompt(messages);
  if (!transcript.trim()) {
    throw new Error('No transcript content available for this conversation.');
  }

  void documents;
  return generateNoteViaBackend({
    messages,
    template,
    patientName,
    providerName,
    specialty,
    conversationId,
    patientId,
    ehrPatientId,
    encounterId,
    generationMode,
    existingSections,
  });
}

/**
 * Progressive-delivery variant of generateNoteFromConversation. Takes the
 * same argument object plus two callbacks:
 *   - `onStart(sections)` — the template's section names, in template
 *     order, before any content arrives.
 *   - `onSection({name, content, tier})` — PRELIMINARY per-section content
 *     as each section finishes (out of order; Assessment/Plan typically
 *     last). Preliminary text may differ slightly from the final note.
 * Resolves with the authoritative final note (the exact normalized shape
 * generateNoteFromConversation returns) from the stream's `complete` event
 * — callers must replace ALL preliminary content with it. Falls back to
 * legacy single-JSON parsing transparently (no progressive events) when the
 * deployed backend doesn't stream. Throws a stream-fallback-eligible error
 * (see isStreamFallbackEligibleError) when the stream dies without a
 * `complete`, so callers can retry once via generateNoteFromConversation.
 */
export async function generateNoteFromConversationStream({
  messages,
  template,
  patientName,
  providerName,
  specialty,
  documents,
  conversationId,
  patientId,
  ehrPatientId,
  encounterId,
  generationMode,
  existingSections,
  onStart,
  onSection,
}: Record<string, any>) {
  const transcript = buildTranscriptForPrompt(messages);
  if (!transcript.trim()) {
    throw new Error('No transcript content available for this conversation.');
  }

  void documents;
  return generateNoteViaBackendStream({
    messages,
    template,
    patientName,
    providerName,
    specialty,
    conversationId,
    patientId,
    ehrPatientId,
    encounterId,
    generationMode,
    existingSections,
    onStart,
    onSection,
  });
}

// ── Backend-draft recovery after a failed generate call ──────
//
// Counterpart to isDraftRecoveryEligibleError above: once a failure is
// classified as recoverable, these helpers look for the draft the backend
// saved despite the client-side failure. After every conversation-linked
// generation the backend mirrors the finished note into
// `ScribeDraft/{conversationId}` — deliberately its OWN collection, never a
// `Conversation` message (billing counts Conversation docs); the client
// still owns the canonical conversation note. The sanctioned read path is
// GET /api/v1/scribe/draft/:conversationId (company-scoped, 404 when there
// is no draft), so "recovery" is: poll that endpoint until a draft appears
// whose server-stamped `updated_at` is fresh enough to be from the attempt
// that just failed.

// How far the draft's server-stamped `updated_at` may sit before the
// client-clock attempt start and still count as "fresh". Two minutes absorbs
// realistic clock skew without letting a draft from an older generation
// masquerade as the one we just asked for. (Bleed across back-to-back
// attempts on the same transcript is fine — the draft content is what the
// failed attempt would have produced anyway.)
const DRAFT_RECOVERY_CLOCK_SKEW_MS = 120_000;

// Polling schedule. Incident data says the draft typically lands seconds
// after the client gives up (45s abort → 53s upsert), but a connection drop
// early in a ~55s generation means it can arrive close to a minute after the
// client saw the error.
export const DRAFT_RECOVERY_MAX_WAIT_MS = 60_000;
export const DRAFT_RECOVERY_POLL_INTERVAL_MS = 5_000;
// When the draft read itself keeps failing, it's almost certainly the same
// outage that killed the generate call — stop hammering it. (A 404 "no draft
// yet" is a normal poll result, not a failure.)
const DRAFT_RECOVERY_MAX_CONSECUTIVE_READ_FAILURES = 2;
// Each poll is a single tiny document read; don't let apiClient's 45s
// default hold a recovery poll open that long on a dead connection.
const DRAFT_FETCH_TIMEOUT_MS = 10_000;

/**
 * Reads the server-side scribe draft for a conversation via
 * GET /api/v1/scribe/draft/:conversationId. Resolves with the raw draft
 * document, or null when none exists (404). Other failures throw.
 */
export async function fetchScribeDraft(conversationId: string) {
  const id = (conversationId || '').trim();
  if (!id) return null;
  const result = await request(
    `/api/v1/scribe/draft/${encodeURIComponent(id)}`,
    {
      passthroughStatuses: [404],
      timeoutMs: DRAFT_FETCH_TIMEOUT_MS,
    },
  );
  if (!result || result.__passthroughStatus === 404) return null;
  return result;
}

/**
 * Maps a `ScribeDraft` document to the note shape
 * generateNoteFromConversation resolves with (templateId/Name, sections,
 * rawTranscription, generatedAt) plus `isRecoveredDraft: true` — or null
 * when the draft can't be trusted as the failed attempt's output: no
 * sections, no usable `updated_at`, or an `updated_at` older than the
 * attempt start minus the clock-skew allowance (i.e. a leftover from an
 * earlier generation).
 *
 * The recovered note has NO conversation message id: the backend draft is
 * insurance storage, not the canonical note. Callers adopt it exactly like a
 * fresh generation — persist via saveGeneratedNoteForSession and use the
 * returned message id.
 */
export function mapScribeDraftToRecoveredNote(
  draft: any,
  {attemptStartedAtMs = null}: Record<string, any> = {},
) {
  if (!draft || typeof draft !== 'object') return null;

  const rawSections = draft.sections;
  if (
    !rawSections ||
    typeof rawSections !== 'object' ||
    Object.keys(rawSections).length === 0
  ) {
    return null;
  }

  // `updated_at` is server-stamped on every upsert; over REST the admin
  // Timestamp serializes as `{_seconds, _nanoseconds}`, which
  // coerceTimestamp understands. Without it we can't prove freshness.
  const updatedAt = coerceTimestamp(draft.updated_at);
  if (!updatedAt) return null;
  if (
    typeof attemptStartedAtMs === 'number' &&
    updatedAt.getTime() < attemptStartedAtMs - DRAFT_RECOVERY_CLOCK_SKEW_MS
  ) {
    return null;
  }

  // Same defense-in-depth post-processing the live generate path applies —
  // idempotent on a well-formed draft.
  const sections: Record<string, any> = {};
  Object.keys(rawSections).forEach(name => {
    sections[name] = cleanSectionContent(name, rawSections[name]);
  });
  reconcileAssessmentAndTreatment(sections);
  appendAiScribeDisclaimer(sections);

  const templateId = draft.template_id || 'soap';
  return {
    templateId,
    templateName: draft.template_name || getTemplateName(templateId),
    sections,
    sectionMeta: {},
    rawTranscription: draft.raw_transcription || '',
    generatedAt: updatedAt,
    codeProvenance: [],
    isRecoveredDraft: true,
  };
}

/**
 * Polls the backend's ScribeDraft store for the note it saved despite a
 * client-side generate failure, resolving with the recovered note or null.
 *
 * `fetchDraft`/`sleep`/`now` are test seams; the default fetcher hits the
 * real endpoint. Never throws: recovery is an opportunistic best-effort
 * layered under an error path — callers fall back to their normal error
 * handling on null.
 */
export async function recoverNoteFromBackendDraft({
  conversationId,
  attemptStartedAtMs = null,
  templateId = null,
  fetchDraft = fetchScribeDraft,
  maxWaitMs = DRAFT_RECOVERY_MAX_WAIT_MS,
  pollIntervalMs = DRAFT_RECOVERY_POLL_INTERVAL_MS,
  sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms)),
  now = () => Date.now(),
}: Record<string, any>) {
  if (!conversationId || typeof fetchDraft !== 'function') return null;

  const startedAt = now();
  log.info('note_draft_recovery_started', {
    conversation_id: conversationId,
    template_id: templateId || null,
    max_wait_ms: maxWaitMs,
  });

  let attempts = 0;
  let consecutiveReadFailures = 0;
  for (;;) {
    attempts += 1;
    try {
      const draft = await fetchDraft(conversationId);
      consecutiveReadFailures = 0;
      const recovered = mapScribeDraftToRecoveredNote(draft, {
        attemptStartedAtMs,
      });
      if (recovered) {
        log.info('note_draft_recovery_succeeded', {
          conversation_id: conversationId,
          template_id: recovered.templateId || null,
          requested_template_id: templateId || null,
          attempts,
          elapsed_ms: now() - startedAt,
        });
        return recovered;
      }
    } catch (err) {
      consecutiveReadFailures += 1;
      log.warn('note_draft_recovery_read_failed', {
        conversation_id: conversationId,
        attempts,
        consecutive_failures: consecutiveReadFailures,
        ...describeError(err),
      });
      if (
        consecutiveReadFailures >= DRAFT_RECOVERY_MAX_CONSECUTIVE_READ_FAILURES
      ) {
        return null;
      }
    }
    if (now() - startedAt + pollIntervalMs > maxWaitMs) {
      log.info('note_draft_recovery_not_found', {
        conversation_id: conversationId,
        template_id: templateId || null,
        attempts,
        elapsed_ms: now() - startedAt,
      });
      return null;
    }
    await sleep(pollIntervalMs);
  }
}

// ── ICD-10 picker search ──────────────────────────────────────

/**
 * Search ICD-10-CM codes for the tap-to-change diagnosis-code picker.
 *
 * `q` accepts a code ("J30.9" / "j309") or free text ("contact dermatitis",
 * common abbreviations like "GERD"); `current` (the code being replaced)
 * pulls in same-family siblings even when `q` is empty. Results come back
 * ranked exact > family > search. The backend 400s when both are empty, so
 * we short-circuit that case here.
 *
 * Deliberately dumb: `request()` already dedupes identical in-flight GETs
 * and serves a short-lived read cache, so no extra caching layer is needed.
 *
 * @returns Array of `{code, description, billable, source}` entries.
 */
export async function searchIcd10Codes({
  q = '',
  current = '',
  limit,
}: {
  q?: string;
  current?: string | null;
  limit?: number;
} = {}): Promise<
  Array<{
    code: string;
    description: string;
    billable: boolean;
    source: 'exact' | 'family' | 'search';
  }>
> {
  const query = (q || '').trim();
  const currentCode = (current || '').trim();
  if (!query && !currentCode) return [];
  const data = await request('/api/v1/scribe/icd10-search', {
    query: {q: query, current: currentCode, limit},
  });
  return Array.isArray(data?.results) ? data.results : [];
}

// ── Post-processing (matches iOS cleanSectionContent) ─────────

function cleanSectionContent(sectionName: any, rawContent: any) {
  let cleaned = (rawContent || '').trim();
  const escaped = sectionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const headerPatterns = [
    new RegExp(`^\\*\\*${escaped}:?\\*\\*\\s*`, 'i'),
    new RegExp(`^##?#?\\s*${escaped}:?\\s*\\n`, 'i'),
    new RegExp(`^${escaped}:\\s*\\n`, 'i'),
  ];
  for (const pattern of headerPatterns) {
    cleaned = cleaned.replace(pattern, '');
  }
  cleaned = cleaned.replace(/\*\*/g, '');
  cleaned = cleaned.replace(/^#{1,3}\s+/gm, '');
  return cleaned.trim();
}

// ── LLM-powered conversation summary ─────────────────────────

/**
 * Generate a concise conversation summary via LLM, matching iOS behavior.
 * Returns the summary string, or an empty string when no useful summary exists.
 */
export async function generateConversationSummary(messages: any) {
  const transcript = buildTranscriptForPrompt(messages);
  if (!transcript.trim()) return '';

  const snippet = transcript.slice(0, 2000);

  try {
    const response = await request('/api/v1/chat/completions', {
      method: 'POST',
      parse: 'raw',
      body: JSON.stringify({
        // `/api/v1/chat/completions` server-forces gpt-4.1 (HIPAA); set
        // explicitly so the intent is clear at the call site.
        model: 'gpt-4.1',
        messages: [
          {
            role: 'system',
            content:
              'You are a clinical documentation assistant. Generate a single-sentence summary (max 120 characters) of the following conversation. Focus ONLY on the clinical context — the chief complaint, symptoms, diagnosis, treatment, medications, or medical topic discussed. Use concise clinical language. Skip greetings, small talk, scheduling/logistics, and session mechanics (do not describe it as a live translation or translated session). If no clinical topic was discussed, respond with "General clinical discussion". No quotes, no preamble.',
          },
          {role: 'user', content: snippet},
        ],
        temperature: 0.2,
        max_tokens: 100,
      }),
    });

    if (!response.ok) return '';

    const payload = await response.json();
    const summary = (payload?.choices?.[0]?.message?.content || '').trim();
    if (!summary) return '';
    return summary.length > 150 ? summary.slice(0, 147) + '...' : summary;
  } catch (err) {
    console.error('Summary generation failed:', err);
    log.warn('conversation_summary_failed', {
      message_count: Array.isArray(messages) ? messages.length : 0,
      ...describeError(err),
    });
    return '';
  }
}
